import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  ArrowLeft,
  Plus,
  Trash2,
  Package,
  CheckCircle,
  Map as MapIcon,
  Ruler,
  Timer,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { Button, Input, Select } from "../components/ui";
import { API_BASE_URL, API_ENDPOINTS } from "../constants/index";
import { useNavigate } from "react-router-dom";
import { inputClassName, labelClassName } from "../styles/utility-classes";
import { useMasterData, useEsanchitCodes } from "../hooks/master-data-hooks";

import GooglePlacesAutocomplete from "../components/google/GooglePlacesAutocomplete";

const CONSIGNMENT_TYPE_OPTIONS = [
  { value: "air", label: "Air" },
  { value: "sea", label: "Sea" },
];

const CreateJobPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [vehiclesByTransporter, setVehiclesByTransporter] = useState({});
  const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false);

  // Load Google Maps API
  useEffect(() => {
    const loadGoogleMaps = () => {
      if (window.google && window.google.maps) {
        setGoogleMapsLoaded(true);
        return;
      }

      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyARm0zb4v57rZQ0XI9QfAb93MDm-wrgZw8&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => setGoogleMapsLoaded(true);
      script.onerror = () => console.error("Failed to load Google Maps API");
      document.head.appendChild(script);
    };
    loadGoogleMaps();
  }, []);

  const {
    customers,
    vendors,
    ports,
    warehouses,
    transporters,
    containers,
    goodsTypes,
    isLoading: masterDataLoading,
    error: masterDataError,
  } = useMasterData();

  // Form state
  const [formDetails, setFormDetails] = useState({
    type: "",
    priority: "",
    customerPO: "",
    description: "",
    tallySheetNo: "",
    chail_job_code: "",
  });

  const esanchitQuery = useEsanchitCodes(formDetails.consignment_type);
  const esanchitOptions = esanchitQuery.data || [];

  const [legs, setLegs] = useState([
    {
      id: `leg-initial-${Date.now()}`,
      expanded: true,
      type: "pickup",
      customer: "",
      vendor: "",
      port: "",
      warehouse: "",
      transporter: "",
      vehicle: "",
      driverName: "",
      driverContact: "",
      origin: "",
      destination: "",
      originPlaceData: null,
      destinationPlaceData: null,
      goodsType: "",
      container: "",
      distance: 0,
      estimatedHours: 0,
      journeyDate: "",
      journeyTime: "",
      specialInstructions: "",
    },
  ]);

  // Calculate distance between two coordinates using Haversine formula
  const calculateDistance = useCallback((lat1, lon1, lat2, lon2) => {
    const R = 6371; // Radius of the Earth in kilometers
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c; // Distance in kilometers
    return Math.round(distance);
  }, []);

  // Create a function to get vehicles for a specific transporter
  const getVehiclesForTransporter = useCallback(async (transporterCode) => {
    if (!transporterCode) return [];

    try {
      const response = await fetch(
        `${API_BASE_URL}${API_ENDPOINTS.TRANSPORTERWISEVEHICLELIST}?transporter_code=${transporterCode}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.status === 200) {
        return result.data.map((item) => ({
          value: item.vehicle_id.toString(),
          label: item.vehicle_no,
          type: item.vehicle_type,
          desc: item.desc,
          capacity: item.max_capacity,
        }));
      }
      return [];
    } catch (error) {
      console.error("Error fetching vehicles:", error);
      return [];
    }
  }, []);

  // Effect to load vehicles when transporters change
  useEffect(() => {
    const uniqueTransporters = [
      ...new Set(legs.map((leg) => leg.transporter).filter(Boolean)),
    ];

    uniqueTransporters.forEach(async (transporterCode) => {
      if (!vehiclesByTransporter[transporterCode]) {
        const vehicles = await getVehiclesForTransporter(transporterCode);
        setVehiclesByTransporter((prev) => ({
          ...prev,
          [transporterCode]: vehicles,
        }));
      }
    });
  }, [legs, getVehiclesForTransporter]);

  // Calculate costs and duration
  const { totalDistance, estimatedDuration } = useMemo(() => {
    const totalDistance = legs.reduce(
      (sum, leg) => sum + (Number(leg.distance) || 0),
      0
    );
    const totalHours = legs.reduce(
      (sum, leg) => sum + (Number(leg.estimatedHours) || 0),
      0
    );
    return {
      totalDistance,
      estimatedDuration: totalHours,
    };
  }, [legs]);

  // Form handlers
  const updateFormData = useCallback((field, value) => {
    setFormDetails((prev) => ({ ...prev, [field]: value }));
  }, []);

  const addLeg = useCallback(() => {
    const newLeg = {
      id: `leg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      expanded: true,
      type: legs.length === 0 ? "pickup" : "delivery",
      customer: "",
      vendor: "",
      port: "",
      warehouse: "",
      transporter: "",
      vehicle: "",
      driverName: "",
      driverContact: "",
      origin: "",
      destination: "",
      goodsType: "",
      container: "",
      distance: 0,
      estimatedHours: 0,
      journeyDate: "",
      journeyTime: "",
      specialInstructions: "",
    };
    setLegs((prev) => [...prev, newLeg]);
  }, [legs.length]);

  const updateLeg = useCallback(
    (index, field, value, additionalData = null) => {
      setLegs((prev) => {
        return prev.map((leg, i) => {
          if (i !== index) return leg;
          const updatedLeg = { ...leg, [field]: value };

          // Handle special field updates
          if (field === "transporter") {
            updatedLeg.vehicle = "";
          } else if (field === "distance" && value > 0) {
            updatedLeg.estimatedHours = Math.round((value / 40) * 10) / 10;
          } else if (field === "origin" && additionalData) {
            updatedLeg.originPlaceData = additionalData;
            if (
              leg.destinationPlaceData &&
              additionalData.lat &&
              additionalData.lng
            ) {
              const distance = calculateDistance(
                additionalData.lat,
                additionalData.lng,
                leg.destinationPlaceData.lat,
                leg.destinationPlaceData.lng
              );
              updatedLeg.distance = distance;
              updatedLeg.estimatedHours = Math.round((distance / 40) * 10) / 10;
            }
          } else if (field === "destination" && additionalData) {
            updatedLeg.destinationPlaceData = additionalData;
            if (
              leg.originPlaceData &&
              additionalData.lat &&
              additionalData.lng
            ) {
              const distance = calculateDistance(
                leg.originPlaceData.lat,
                leg.originPlaceData.lng,
                additionalData.lat,
                additionalData.lng
              );
              updatedLeg.distance = distance;
              updatedLeg.estimatedHours = Math.round((distance / 40) * 10) / 10;
            }
          }
          return updatedLeg;
        });
      });
    },
    [calculateDistance]
  );

  const removeLeg = useCallback((index) => {
    setLegs((prev) => {
      if (prev.length > 1) {
        return prev.filter((_, i) => i !== index);
      }
      return prev;
    });
  }, []);

  const toggleLegExpansion = useCallback((index) => {
    setLegs((prev) =>
      prev.map((leg, i) =>
        i === index ? { ...leg, expanded: !leg.expanded } : leg
      )
    );
  }, []);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const legsPayload = legs.map((leg) => {
        const selectedVehicle = (
          vehiclesByTransporter[leg.transporter] || []
        ).find((v) => v.value === leg.vehicle);

        return {
          vehicle_id: parseInt(leg.vehicle) || null,
          container_id: parseInt(leg.container) || null,
          goods_id: leg.goodsType || 0,
          weight: leg.weight || "0",
          start_date: leg.journeyDate,
          end_date: "2025-01-01",
          status: 2,
          vendor_code: leg.vendor,
          transpoter_code: leg.transporter,
          driver_contact_no: leg.driverContact,
          driver_name: leg.driverName,
          created_by: "admin",
          leg_type: leg.type,
          vehicle_type: selectedVehicle?.type || "",
          port_id: parseInt(leg.port) || null,
          origin: leg.origin,
          destination: leg.destination,
          remarks: leg.specialInstructions || "remarks",
          customer_code: leg.customer,
          wh_id: parseInt(leg.warehouse) || null,
          originPlaceData: leg.originPlaceData,
          destinationPlaceData: leg.destinationPlaceData,
          distance: leg.distance,
          estimatedHours: leg.estimatedHours,
        };
      });

      const jobPayload = {
        jobData: {
          status: 1,
          job_type: formDetails.type,
          job_create_date: new Date().toISOString(),
          priority: formDetails.priority,
          job_end_date: "2000-01-01T00:00:00.000Z",
          created_by: "admin",
          customer_po: formDetails.customerPO,
          tally_no: formDetails.tallySheetNo,
          esanchit_code: formDetails.eSanchitCode || null,
          consignment_type: formDetails.consignment_type || null,
          chail_job_code: formDetails.chail_job_code || "",
          job_desc: formDetails.description,
          total_distance: totalDistance,
          total_legs: legs.length,
          total_duration: estimatedDuration,
        },
        legsData: legsPayload,
      };

      const response = await fetch(
        `${API_BASE_URL}${API_ENDPOINTS.JOBSCREATE}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
          body: JSON.stringify(jobPayload),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const res = await response.json();
      if (res.status === 201) {
        navigate("/jobs");
      } else {
        console.error("Error creating job:", res);
        alert("Error creating job. Please try again.");
      }
    } catch (error) {
      console.error("Error creating job:", error);
      alert("Error creating job. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Show loading state
  if (masterDataLoading) {
    return (
      <div className="p-6 max-w-7xl mx-auto flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading master data...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (masterDataError) {
    return (
      <div className="p-6 max-w-7xl mx-auto flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600 mb-4">Error loading master data</p>
          <Button onClick={() => window.location.reload()}>Retry</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <Button
            onClick={() => window.history.back()}
            className="mr-4 text-black hover:text-gray-800 bg-white border hover:bg-gray-50"
          >
            <ArrowLeft className="w-4 h-4 text-black" />
          </Button>
        </div>

        <div className="flex items-center gap-3">
          <Button
            className="text-xl shadow-xl"
            onClick={handleSubmit}
            loading={loading}
          >
            <CheckCircle className="w-6 h-6 mr-4" />
            Create Job
          </Button>
        </div>
      </div>

      <div className="space-y-8">
        {/* Job Information Section */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
            <Package className="w-5 h-5 mr-2 text-blue-600" />
            Job Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 pb-4">
            <div className="space-y-1">
              <label className={labelClassName}>Job Type *</label>
              <Select
                value={formDetails.type}
                onChange={(e) => updateFormData("type", e.target.value)}
                options={[
                  { value: "import", label: "Import" },
                  { value: "export", label: "Export" },
                  { value: "domestic", label: "Domestic" },
                  { value: "vendor", label: "Vendor" },
                  { value: "customer", label: "Customer" },
                  { value: "general", label: "General" },
                ]}
                placeholder="Select Job Type"
              />
            </div>

            <div className="space-y-1">
              <label className={labelClassName}>Priority *</label>
              <Select
                value={formDetails.priority}
                onChange={(e) => updateFormData("priority", e.target.value)}
                options={[
                  { value: "High", label: "High" },
                  { value: "Medium", label: "Medium" },
                  { value: "Low", label: "Low" },
                ]}
                placeholder="Select Priority"
              />
            </div>

            <Input
              label="Customer PO"
              type="text"
              defaultValue={formDetails.customerPO}
              onBlur={(e) => updateFormData("customerPO", e.target.value)}
              placeholder="Enter PO number"
            />

            <Input
              label="Tally Sheet No"
              type="text"
              defaultValue={formDetails.tallySheetNo}
              onBlur={(e) => updateFormData("tallySheetNo", e.target.value)}
              placeholder="Enter Tally Sheet No"
            />

            {/* + E-Sanchit Code */}
            <div className="space-y-1">
              <label className={labelClassName}>E-Sanchit Code</label>
              <Select
                value={formDetails.eSanchitCode}
                onChange={(e) => updateFormData("eSanchitCode", e.target.value)}
                options={[
                  { value: "", label: "Select E-Sanchit Code", disabled: true },
                  ...esanchitOptions.map((o) => ({
                    value: o.value,
                    label: o.label,
                  })),
                ]}
                placeholder="Select E-Sanchit"
              />
            </div>

            {/* + Consignment Type */}
            <div className="space-y-1">
              <label className={labelClassName}>Consignment Type</label>
              <Select
                value={formDetails.consignment_type}
                onChange={(e) => {
                  // clear previously selected e-sanchit code when changing consignment type
                  updateFormData("consignment_type", e.target.value);
                  updateFormData("eSanchitCode", "");
                }}
                options={[
                  {
                    value: "",
                    label: "Select Consignment Type",
                    disabled: true,
                  },
                  ...CONSIGNMENT_TYPE_OPTIONS,
                ]}
              />
            </div>

            {/* + Chail Code */}
            <div className="space-y-1">
              <label className={labelClassName}>Chail Job Code</label>
              <input
                type="text"
                value={formDetails.chail_job_code}
                onChange={(e) =>
                  updateFormData("chail_job_code", e.target.value)
                }
                placeholder="Enter Chail Code"
                className={inputClassName}
              />
            </div>
          </div>

          <Input
            label="Job Description"
            type="text"
            value={formDetails.description}
            onChange={(e) => updateFormData("description", e.target.value)}
            placeholder="Brief description of the job..."
          />
        </div>

        {/* Route & Legs Section */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <MapIcon className="w-5 h-5 mr-2 text-blue-600" />
              Route & Legs Configuration
            </h2>
            <Button onClick={addLeg} variant="outline">
              <Plus className="w-4 h-4 mr-2" />
              Add Leg
            </Button>
          </div>

          <div className="space-y-4">
            {legs.map((leg, index) => (
              <div
                key={leg.id}
                className="border border-gray-200 rounded-lg overflow-hidden"
              >
                {/* Leg Header */}
                <div
                  className="bg-gray-50 p-4 cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => toggleLegExpansion(index)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-medium ${
                          leg.type === "pickup"
                            ? "bg-green-500"
                            : leg.type === "delivery"
                              ? "bg-blue-500"
                              : "bg-orange-500"
                        }`}
                      >
                        {index + 1}
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900 capitalize">
                          Leg {index + 1} - {leg.type}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {leg.origin || "Origin not set"} →{" "}
                          {leg.destination || "Destination not set"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="text-sm text-gray-500">
                        {leg.distance} km • {leg.estimatedHours} hrs
                      </div>
                      {legs.length > 1 && (
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            removeLeg(index);
                          }}
                          variant="ghost"
                          className="text-red-600 hover:text-red-700 p-2"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                      {leg.expanded ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Leg Content */}
                {leg.expanded && (
                  <div className="p-6 space-y-6">
                    {/* Basic Information */}
                    <div>
                      <h4 className="font-medium text-gray-900 mb-4">
                        Basic Information
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div className="space-y-1">
                          <label className={labelClassName}>Leg Type</label>
                          <Select
                            value={leg.type}
                            onChange={(e) =>
                              updateLeg(index, "type", e.target.value)
                            }
                            options={[
                              { value: "pickup", label: "Pickup" },
                              { value: "delivery", label: "Delivery" },
                              { value: "transit", label: "Transit" },
                            ]}
                          />
                        </div>

                        <div className="space-y-1">
                          <label className={labelClassName}>Goods Type</label>
                          <Select
                            value={leg.goodsType}
                            onChange={(e) =>
                              updateLeg(index, "goodsType", e.target.value)
                            }
                            options={[
                              {
                                value: "",
                                label: "Select Goods Type",
                                disabled: true,
                              },
                              ...goodsTypes.map((option) => ({
                                value: option.value,
                                label: option.label,
                              })),
                            ]}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Route Information */}
                    <div>
                      <h4 className="font-medium text-gray-900 mb-4">
                        Route Information
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        {googleMapsLoaded ? (
                          <GooglePlacesAutocomplete
                            label="Origin"
                            value={leg.origin}
                            onChange={(e, placeData) =>
                              updateLeg(
                                index,
                                "origin",
                                e.target.value,
                                placeData
                              )
                            }
                            placeholder="Enter pickup location"
                            required
                          />
                        ) : (
                          <div className="space-y-1">
                            <label className={labelClassName}>Origin *</label>
                            <input
                              type="text"
                              value={leg.origin}
                              onChange={(e) =>
                                updateLeg(index, "origin", e.target.value)
                              }
                              placeholder="Enter pickup location"
                              className={inputClassName}
                            />
                          </div>
                        )}

                        {googleMapsLoaded ? (
                          <GooglePlacesAutocomplete
                            label="Destination"
                            value={leg.destination}
                            onChange={(e, placeData) =>
                              updateLeg(
                                index,
                                "destination",
                                e.target.value,
                                placeData
                              )
                            }
                            placeholder="Enter delivery location"
                            required
                          />
                        ) : (
                          <div className="space-y-1">
                            <label className={labelClassName}>
                              Destination *
                            </label>
                            <input
                              type="text"
                              value={leg.destination}
                              onChange={(e) =>
                                updateLeg(index, "destination", e.target.value)
                              }
                              placeholder="Enter delivery location"
                              className={inputClassName}
                            />
                          </div>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="space-y-1">
                          <label className={labelClassName}>
                            Distance (km)
                          </label>
                          <input
                            type="number"
                            value={leg.distance}
                            onChange={(e) =>
                              updateLeg(index, "distance", e.target.value)
                            }
                            placeholder="0"
                            className={inputClassName}
                          />
                        </div>

                        <div className="space-y-1">
                          <label className={labelClassName}>
                            Est. Duration
                          </label>
                          <div className="px-3 py-2 bg-blue-50 border border-blue-300 rounded-lg">
                            <span className="text-blue-800 font-medium flex items-center">
                              <Timer className="w-4 h-4 mr-2" />
                              {leg.estimatedHours} hours
                            </span>
                          </div>
                        </div>

                        <div className="space-y-1">
                          <label className={labelClassName}>
                            Journey Date <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="date"
                            value={leg.journeyDate}
                            onChange={(e) =>
                              updateLeg(index, "journeyDate", e.target.value)
                            }
                            className={inputClassName}
                            required
                          />
                        </div>

                        <div className="space-y-1">
                          <label className={labelClassName}>
                            Journey Time <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="time"
                            value={leg.journeyTime}
                            onChange={(e) =>
                              updateLeg(index, "journeyTime", e.target.value)
                            }
                            className={inputClassName}
                            required
                          />
                        </div>
                      </div>
                    </div>

                    {/* Stakeholders */}
                    <div>
                      <h4 className="font-medium text-gray-900 mb-4">
                        Stakeholders
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="space-y-1">
                          <label className={labelClassName}>Customer</label>
                          <Select
                            value={leg.customer}
                            onChange={(e) =>
                              updateLeg(index, "customer", e.target.value)
                            }
                            options={[
                              {
                                value: "",
                                label: "Select Customer",
                                disabled: true,
                              },
                              ...customers.map((option) => ({
                                value: option.value,
                                label: option.label,
                              })),
                            ]}
                          />
                        </div>

                        <div className="space-y-1">
                          <label className={labelClassName}>Vendor</label>
                          <Select
                            value={leg.vendor}
                            onChange={(e) =>
                              updateLeg(index, "vendor", e.target.value)
                            }
                            options={[
                              {
                                value: "",
                                label: "Select Vendor",
                                disabled: true,
                              },
                              ...vendors.map((option) => ({
                                value: option.value,
                                label: option.label,
                              })),
                            ]}
                          />
                        </div>

                        <div className="space-y-1">
                          <label className={labelClassName}>Port</label>
                          <Select
                            value={leg.port}
                            onChange={(e) =>
                              updateLeg(index, "port", e.target.value)
                            }
                            options={[
                              {
                                value: "",
                                label: "Select Port",
                                disabled: true,
                              },
                              ...ports.map((option) => ({
                                value: option.value,
                                label: option.label,
                              })),
                            ]}
                          />
                        </div>

                        <div className="space-y-1">
                          <label className={labelClassName}>Warehouse</label>
                          <Select
                            value={leg.warehouse}
                            onChange={(e) =>
                              updateLeg(index, "warehouse", e.target.value)
                            }
                            options={[
                              {
                                value: "",
                                label: "Select Warehouse",
                                disabled: true,
                              },
                              ...warehouses.map((option) => ({
                                value: option.value,
                                label: option.label,
                              })),
                            ]}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Transport Details */}
                    <div>
                      <h4 className="font-medium text-gray-900 mb-4">
                        Transport Details
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div className="space-y-1">
                          <label className={labelClassName}>Transporter</label>
                          <Select
                            value={leg.transporter}
                            onChange={(e) =>
                              updateLeg(index, "transporter", e.target.value)
                            }
                            options={[
                              {
                                value: "",
                                label: "Select Transporter",
                                disabled: true,
                              },
                              ...transporters.map((option) => ({
                                value: option.value,
                                label: option.label,
                              })),
                            ]}
                          />
                        </div>

                        <div className="space-y-1">
                          <label className={labelClassName}>Vehicle</label>
                          <Select
                            value={leg.vehicle}
                            onChange={(e) =>
                              updateLeg(index, "vehicle", e.target.value)
                            }
                            disabled={!leg.transporter}
                            options={[
                              {
                                value: "",
                                label: "Select Vehicle",
                                disabled: true,
                              },
                              ...(
                                vehiclesByTransporter[leg.transporter] || []
                              ).map((v) => ({
                                value: v.value,
                                label: v.label,
                              })),
                            ]}
                          />
                        </div>

                        {leg.vehicle && (
                          <div className="space-y-1">
                            <label className={labelClassName}>
                              Vehicle Details
                            </label>
                            <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg">
                              <span className="text-gray-900 font-medium">
                                {(() => {
                                  const vehicle = (
                                    vehiclesByTransporter[leg.transporter] || []
                                  ).find((v) => v.value === leg.vehicle);
                                  return vehicle
                                    ? `${vehicle.type} - ${vehicle.desc}`
                                    : "Vehicle Details";
                                })()}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Driver Details - Now as inputs */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        <div className="space-y-1">
                          <label className={labelClassName}>Driver Name</label>
                          <input
                            type="text"
                            value={leg.driverName}
                            onChange={(e) =>
                              updateLeg(index, "driverName", e.target.value)
                            }
                            placeholder="Enter driver name"
                            className={inputClassName}
                          />
                        </div>

                        <div className="space-y-1">
                          <label className={labelClassName}>
                            Driver Contact
                          </label>
                          <input
                            type="text"
                            value={leg.driverContact}
                            onChange={(e) =>
                              updateLeg(index, "driverContact", e.target.value)
                            }
                            placeholder="Enter driver contact number"
                            className={inputClassName}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Container Details */}
                    <div>
                      <h4 className="font-medium text-gray-900 mb-4">
                        Container Details
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="space-y-1">
                          <label className={labelClassName}>Container</label>
                          <select
                            value={leg.container}
                            onChange={(e) =>
                              updateLeg(index, "container", e.target.value)
                            }
                            className={inputClassName}
                          >
                            <option value="">Select Container</option>
                            {containers.map((c) => (
                              <option key={c.value} value={c.value}>
                                {c.label} ({c.type.toUpperCase()}) - {c.owner}
                              </option>
                            ))}
                          </select>
                        </div>

                        {leg.container && (
                          <>
                            <div className="space-y-1">
                              <label className={labelClassName}>
                                Container Type
                              </label>
                              <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg">
                                <span className="text-gray-900 font-medium">
                                  {containers
                                    .find((c) => c.value === leg.container)
                                    ?.type.toUpperCase() || "N/A"}
                                </span>
                              </div>
                            </div>

                            <div className="space-y-1">
                              <label className={labelClassName}>
                                Free Time
                              </label>
                              <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg">
                                <span className="text-gray-900 font-medium">
                                  {containers.find(
                                    (c) => c.value === leg.container
                                  )?.freeTime || "N/A"}{" "}
                                  hours
                                </span>
                              </div>
                            </div>

                            <div className="space-y-1">
                              <label className={labelClassName}>
                                Detention Rate
                              </label>
                              <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg">
                                <span className="text-gray-900 font-medium">
                                  ₹
                                  {containers.find(
                                    (c) => c.value === leg.container
                                  )?.detentionRate || "N/A"}
                                  /hour
                                </span>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Special Instructions */}
                    <div className="space-y-1">
                      <label className={labelClassName}>
                        Special Instructions
                      </label>
                      <input
                        type="text"
                        value={leg.specialInstructions}
                        onChange={(e) =>
                          updateLeg(
                            index,
                            "specialInstructions",
                            e.target.value
                          )
                        }
                        placeholder="Enter any special handling instructions for this leg..."
                        className={inputClassName}
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Route Summary */}
          <div className="border-t border-gray-200 pt-6 mt-6">
            <h3 className="font-medium text-gray-900 mb-4">Route Summary</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-blue-600 font-medium">
                      Total Legs
                    </p>
                    <p className="text-2xl font-bold text-blue-800">
                      {legs.length}
                    </p>
                  </div>
                  <MapIcon className="w-8 h-8 text-blue-600" />
                </div>
              </div>

              <div className="bg-green-50 p-4 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-600 font-medium">
                      Total Distance
                    </p>
                    <p className="text-2xl font-bold text-green-800">
                      {totalDistance} km
                    </p>
                  </div>
                  <Ruler className="w-8 h-8 text-green-600" />
                </div>
              </div>

              <div className="bg-orange-50 p-4 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-orange-600 font-medium">
                      Est. Duration
                    </p>
                    <p className="text-2xl font-bold text-orange-800">
                      {parseFloat(estimatedDuration).toFixed(2)} h
                    </p>
                  </div>
                  <Timer className="w-8 h-8 text-orange-600" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Pre-submission Checklist */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
          <div className="flex items-start">
            <CheckCircle className="w-6 h-6 text-green-600 mt-0.5 mr-4" />
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 mb-3">
                Job Creation Summary
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                      Job type:{" "}
                      <span className="font-medium ml-1 capitalize">
                        {formDetails.type}
                      </span>
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                      Priority:{" "}
                      <span className="font-medium ml-1 capitalize">
                        {formDetails.priority}
                      </span>
                    </li>
                  </ul>
                </div>
                <div>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                      Total legs configured:{" "}
                      <span className="font-medium ml-1">{legs.length}</span>
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                      Ready for submission
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <Button
            className="text-xl shadow-xl"
            onClick={handleSubmit}
            loading={loading}
          >
            <CheckCircle className="w-6 h-6 mr-4" />
            Create Job
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CreateJobPage;
